import type { AnalyticsDashboardResponse } from '@/api/generated';

import {
  ROWS_WITH_CHANGE,
  type CardId,
  type DashboardData,
  type RowId,
  type RowWithChange,
  type TrendTab,
} from './dashboardData';

/**
 * The analytics reply, as the page wants it.
 *
 * The reply's keys are the backend's event types (`campaign.viewed`) and
 * fixed card names; the page's ids are its own. This is the one place the two
 * vocabularies meet. A metric the backend did not send reads as zero, so a
 * registry that shrinks never blanks a row.
 *
 * `petition.*`, `event.*`, `active_petitions` and `upcoming_events` arrive
 * and are not listed: hidden until the client approves that scope (settled
 * 9 October 2026). Adding them is a row in `dashboardData.ts` and a line here.
 */

const CARD_SOURCES: Record<CardId, keyof AnalyticsDashboardResponse['cards']> = {
  totalSupporters: 'total_supporters',
  arcViews: 'arc_views',
  contentViews: 'content_views',
  advocacyActions: 'advocacy_actions',
};

type Supporters = AnalyticsDashboardResponse['supporters'];

/** Where each row's number is: an event type, or a field of `supporters`. */
const ROW_SOURCES: Record<RowId, { metric: string } | { supporters: keyof Supporters }> =
  {
    campaignViews: { metric: 'campaign.viewed' },
    callClicks: { metric: 'campaign.call_clicked' },
    emailClicks: { metric: 'campaign.email_clicked' },
    guideViews: { metric: 'guide.viewed' },
    surveyViews: { metric: 'survey.viewed' },
    surveySubmissions: { metric: 'survey.submitted' },
    pageViews: { metric: 'page.viewed' },
    documentViews: { metric: 'document.viewed' },
    documentDownloads: { metric: 'document.downloaded' },
    volunteerClicks: { metric: 'volunteer_link.clicked' },
    volunteerSubmissions: { metric: 'volunteer_form.submitted' },
    fundraisingClicks: { metric: 'fundraising_link.clicked' },
    newSupporters: { supporters: 'new' },
    newMembers: { supporters: 'members_new' },
    totalMembers: { supporters: 'members_total' },
    invitesAccepted: { supporters: 'invites_accepted' },
    notificationOpens: { metric: 'notification.opened' },
    notificationClicks: { metric: 'notification.clicked' },
    orgContentViews: { metric: 'org_content.viewed' },
  };

/**
 * Each row that shows a change, against its own previous (P5). The
 * followers-only `supporters.previous` is no row's previous (P2).
 */
const PREVIOUS_SOURCES: Record<RowWithChange, keyof Supporters> = {
  newSupporters: 'previous_new',
  totalMembers: 'members_previous',
  newMembers: 'members_previous_new',
};

const TABS: readonly TrendTab[] = ['overview', 'advocacy', 'content', 'notifications'];

export function toDashboardData(reply: AnalyticsDashboardResponse): DashboardData {
  const cards = Object.fromEntries(
    (Object.keys(CARD_SOURCES) as CardId[]).map((id) => {
      const card = reply.cards[CARD_SOURCES[id]];
      return [id, { current: card.value, previous: card.previous }];
    }),
  ) as DashboardData['cards'];

  const rows = Object.fromEntries(
    (Object.keys(ROW_SOURCES) as RowId[]).map((id) => {
      const source = ROW_SOURCES[id];
      const value =
        'metric' in source
          ? (reply.metrics[source.metric] ?? 0)
          : reply.supporters[source.supporters];
      return [id, value];
    }),
  ) as DashboardData['rows'];

  const previousRows = Object.fromEntries(
    ROWS_WITH_CHANGE.map((id) => [id, reply.supporters[PREVIOUS_SOURCES[id]]]),
  ) as DashboardData['previousRows'];

  const series = Object.fromEntries(
    TABS.map((tab) => [
      tab,
      reply.series.map((point) => ({ date: point.day, value: point[tab] })),
    ]),
  ) as DashboardData['series'];

  const live = reply.current_activity;
  return {
    range: { from: reply.range.from, to: reply.range.to },
    previous: { from: reply.range.previous_from, to: reply.range.previous_to },
    cards,
    rows,
    previousRows,
    series,
    activity: {
      activeCampaigns: live.active_campaigns,
      currentGuides: live.current_guides,
      openSurveys: live.open_surveys,
      scheduledNotifications: live.scheduled_notifications,
      pendingInvitations: live.pending_invitations,
    },
  };
}
